import styled, { css } from 'styled-components';
import { NavLink } from 'react-router-dom';
import SVG from '@components/1-atoms/SVG';
import { RAIL_LOGO_CENTER } from '@components/SidebarRail';

const BRAND_MARK_PX = 48;

/**
 * The rail is full-height of the sidebar slot (which is sized to
 * {@link SIDEBAR_WIDTH_NARROW} by BaseLayout on the editor route). Width
 * `100%` so the rail stretches to fill the slot regardless of the exact
 * pixel value chosen.
 *
 * The root deliberately does NOT scroll: the per-node quick-add list
 * inside {@link QuickAddStack} owns vertical scrolling so the brand,
 * Search/+, and the trailing "More" 3-dots stay pinned to the rail's
 * top and bottom edges. Without this split, a tall catalog pushes the
 * 3-dots off the bottom of the rail and the user loses the only
 * affordance for opening the full library.
 */
export const RailRoot = styled.aside`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing?.level3 ?? '8px'};
  padding: ${RAIL_LOGO_CENTER - BRAND_MARK_PX / 2}px 10px 12px;
  background: ${({ theme: { colors = {} } }) => colors.surface ?? '#0E0F18'};
`;

/**
 * Top of the rail — Aibuild logo + small "OS" workspace label, stacked.
 * The wordmark is the link to the dashboard (`/`); the label is a button
 * that expands the rail back to the full NavigationBar AND auto-pops the
 * ProductSwitcher dropdown.
 */
export const BrandStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  position: relative;
  flex-shrink: 0;
`;

export const BrandMark = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${BRAND_MARK_PX}px;
  height: ${BRAND_MARK_PX}px;
  border-radius: 12px;
  text-decoration: none;
  color: inherit;

  &:hover {
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainerHigh ?? '#1F2030'};
  }
`;

/**
 * The Aibuild logo. We use the existing SVG sprite — picking the inverted
 * `logo-white` variant on light theme so it stays legible against the
 * drawer surface, matching the global NavigationDrawer treatment.
 */
export const Logo = styled(SVG).attrs(({ theme }) => ({
  name:
    theme && typeof theme === 'object' && 'isLightTheme' in theme &&
    (theme as { isLightTheme?: boolean }).isLightTheme
      ? 'logo-white'
      : 'logo',
}))`
  width: 40px;
  height: 40px;
`;

/**
 * Workspace switcher pill. Clicking it expands the rail AND signals the
 * ProductSwitcher (mounted inside the expanded NavigationBar) to pop its
 * dropdown so the user can pick a workspace immediately. Stays
 * interactive even when the rail is in its `disabled` (published) state
 * — switching workspace is a navigation, not a process mutation.
 */
export const WorkspaceLabel = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 24px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.2px;
  line-height: 1;
  cursor: pointer;
  user-select: none;
  transition: background 120ms ease, color 120ms ease;

  &:hover {
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainerHigh ?? '#1F2030'};
    color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
    outline-offset: 2px;
  }
`;

/**
 * The icon-button stack below the brand. Tight gap so the rail reads as a
 * single column of related controls.
 */
export const IconStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing?.level2 ?? '4px'};
  margin-top: ${({ theme: { spacing } }) => spacing?.level2 ?? '4px'};
  width: 100%;
  flex-shrink: 0;
`;

/**
 * Inner section that houses the per-node-type quick-add icons. Same
 * stacked layout as IconStack but with a hairline separator above so the
 * primary actions (Search, +) and the per-type quick-adds read as
 * distinct groups.
 */
/**
 * The per-node-type quick-add scroll region. Absorbs the rail's vertical
 * slack and scrolls when the catalog is taller than the available space.
 * The trailing {@link MoreSlot} sits OUTSIDE this container so the
 * 3-dots stays visible at the bottom of the rail no matter how much the
 * catalog grows.
 *
 * overflow-y: auto forces overflow-x: auto per the CSS spec, which
 * would clip the right-popping per-button Tooltips — every Tooltip
 * inside therefore renders in portal mode so the tip escapes this
 * scroll container into document.body.
 *
 * The scrollbar is hidden visually because the rail is only 72px wide
 * and a visible scrollbar would steal a real chunk of the icon column;
 * users still scroll via wheel / trackpad / keyboard. A subtle
 * bottom-edge mask hints at "more below" when content overflows.
 */
export const QuickAddStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing?.level2 ?? '4px'};
  width: 100%;
  padding-top: 6px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  border-top: 1px solid
    ${({ theme: { colors = {} } }) =>
      colors.outlineVariant ?? 'rgba(70,69,85,0.5)'};
`;

/**
 * Trailing slot for the "More" (3-dots) button. Lives INSIDE
 * {@link QuickAddStack} so it sits in the column flow right after the
 * last quick-add item — when the list is short, the dots hug the
 * bottom of the list with the stack's natural 4px gap rather than
 * floating at the bottom of the rail with a big gap of empty rail
 * above them.
 *
 * `position: sticky; bottom: 0` keeps the affordance visible when the
 * catalog is taller than the available space and starts scrolling: the
 * dots stay painted at the bottom of the scroll container while items
 * scroll past behind them. The opaque {@code background: surface}
 * covers those scrolled items so they don't bleed through the button.
 *
 * `flex-shrink: 0` and `align-self: stretch` together stop the column's
 * shrink algorithm from squashing the slot when the list overflows —
 * without them the sticky element collapses to its content's min size
 * and the background covers less than the button's footprint, leaving
 * a 1–2px sliver of scrolling content visible above the button.
 */
export const MoreSlot = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  flex-shrink: 0;
  align-self: stretch;
  background: ${({ theme: { colors = {} } }) => colors.surface ?? '#0E0F18'};
`;

interface RailButtonProps {
  $isOpen?: boolean;
}

const railButtonBase = css<RailButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 0;
  cursor: pointer;
  background: transparent;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  transition: background 120ms ease, color 120ms ease;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainerHigh ?? '#1F2030'};
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    filter: grayscale(0.4);

    &:hover {
      background: transparent;
    }
  }

  & > svg {
    width: 20px;
    height: 20px;
  }
`;

/**
 * The Search rail icon — tonal-style chip (slightly tinted resting state)
 * that pins to the {@code is-active} treatment while the flyout is open
 * so the user has a clear "this is the toggle" affordance.
 *
 * Mirrors the prototype's `.nav__rail-icon--wf-search`.
 */
export const SearchButton = styled.button<RailButtonProps>`
  ${railButtonBase}
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerHigh ?? '#1F2030'};
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};

  &:hover {
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainerHighest ?? '#34343F'};
    color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  }

  ${({ $isOpen, theme: { colors = {} } }) =>
    $isOpen &&
    css`
      background: ${colors.surfaceContainerHighest ?? '#34343F'} !important;
      color: ${colors.onSurface ?? '#E6E6F2'} !important;
    `}
`;

/**
 * The "+" / Add rail icon — filled-style chip with the brand's lavender
 * "primary emphasis" colour pair, matching the prototype's
 * `.nav__rail-icon--wf-add`. We read from the theme's `primaryEmphasis` /
 * `onPrimaryEmphasis` tokens with `colors.primary` / `colors.onPrimary` as
 * fallbacks so the button still looks right on themes that haven't picked
 * up the emphasis triplet yet.
 */
export const AddButton = styled.button<RailButtonProps>`
  ${railButtonBase}
  background: ${({ theme: { colors = {} } }) =>
    colors.primaryEmphasis ?? colors.primaryFixed ?? '#CFC9F5'};
  color: ${({ theme: { colors = {} } }) =>
    colors.onPrimaryEmphasis ?? colors.onPrimaryFixed ?? '#1E1A8A'};

  &:hover {
    background: ${({ theme: { colors = {} } }) =>
      colors.primaryEmphasisHover ?? colors.primaryFixedDim ?? '#BFB8F0'};
    color: ${({ theme: { colors = {} } }) =>
      colors.onPrimaryEmphasis ?? colors.onPrimaryFixed ?? '#1E1A8A'};
  }

  &:disabled:hover {
    background: ${({ theme: { colors = {} } }) =>
      colors.primaryEmphasis ?? colors.primaryFixed ?? '#CFC9F5'};
  }
`;

/**
 * Generic rail icon-button. Used for the per-node-type quick-add buttons
 * and the "More" trailing button.
 */
export const RailButton = styled.button<RailButtonProps>`
  ${railButtonBase}
`;

/**
 * The Tooltip-wrapper renders a `display: inline-flex` span around the
 * button. In a column flex container that span shrinks to its child, but
 * we still want the button's outline ring to be the click target — so we
 * give the wrapper a fixed 40px footprint to match the button beneath.
 *
 * Pulled into a styled span so consumers don't have to thread a className
 * through the `@aibuild/ui` Tooltip (the Tooltip's wrapper accepts
 * `className`).
 */
export const TooltipFrame = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;

`;

/**
 * Visually-hidden text used to reinforce a rail button's intent for
 * screen readers when the visible glyph alone might be ambiguous (e.g.
 * "drag me" on the Input quick-add).
 */
export const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;
